/**
 * ClientsPage
 *
 * Administrative page for viewing and managing clients. Live, searchable, paginated
 * list backed by GET /api/clients (Spec 178 US1). Client identity is synced from
 * PersonalMed (US5) — this page is read/navigate + filter; there is no "create client".
 *
 * @module admin
 * @spec 178-client-management (US1) · conformed to the Spec 197 design blueprint
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Building2,
  Search,
  ChevronRight,
  Users,
  FolderOpen,
  Activity,
} from 'lucide-react';
import { systemHelp } from '../../data/help/systemHelp';
import {
  PageLayout,
  Card,
  Button,
  Input,
  Select,
  Badge,
  EmptyState,
  ErrorState,
  LoadingState,
  AppTooltip,
  PageHelpPanel,
} from '../../components/ui';
import type { BadgeVariant } from '../../components/ui';
import { clientApi, type ClientListItem } from '../../services/clientApi';
import { formatEasternDate } from '@/utils/dateFormatter';

export interface Client {
  id: string;
  name: string;
  code: string;
  status: 'active' | 'inactive' | 'pending';
  capacityCount: number;
  workspaceCount: number;
  lastActivity: string;
  createdAt: string;
}

export interface ClientsPageProps {
  /** Optional pre-supplied clients (used by tests). When omitted, the page self-fetches. */
  clients?: Client[];
  isLoading?: boolean;
  error?: Error | null;
}

const STATUS_VARIANT: Record<Client['status'], BadgeVariant> = {
  active: 'success',
  inactive: 'secondary',
  pending: 'warning',
};

const PAGE_SIZE = 10;

const toClient = (r: ClientListItem): Client => ({
  id: r.id,
  name: r.name,
  code: r.code,
  status: r.isActive ? 'active' : 'inactive',
  capacityCount: r.capacityCount,
  workspaceCount: r.workspaceCount,
  lastActivity: r.lastActivity,
  createdAt: r.lastActivity,
});

const ClientCard: React.FC<{ client: Client }> = ({ client }) => (
  <Link to={`/admin/clients/${client.id}`} className="block">
    <Card isHoverable className="p-4">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-[var(--color-bg-subtle)] p-2">
            <Building2 className="h-5 w-5 text-[var(--color-accent)]" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-[var(--color-text-primary)]">{client.name}</h3>
              <AppTooltip title={`Client is currently ${client.status}`}>
                <Badge variant={STATUS_VARIANT[client.status]} className="capitalize">
                  {client.status}
                </Badge>
              </AppTooltip>
            </div>
            <p className="text-sm text-[var(--color-text-secondary)]">Code: {client.code}</p>
          </div>
        </div>
        <ChevronRight className="h-5 w-5 text-[var(--color-text-secondary)]" />
      </div>

      <div className="mt-4 flex items-center gap-6 text-sm text-[var(--color-text-secondary)]">
        <div className="flex items-center gap-1.5">
          <Activity className="h-4 w-4" />
          <span>{client.capacityCount} capacities</span>
        </div>
        <div className="flex items-center gap-1.5">
          <FolderOpen className="h-4 w-4" />
          <span>{client.workspaceCount} workspaces</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Users className="h-4 w-4" />
          <span>Last active: {formatEasternDate(client.lastActivity)}</span>
        </div>
      </div>
    </Card>
  </Link>
);

export const ClientsPage: React.FC<ClientsPageProps> = ({ clients: clientsProp, isLoading: loadingProp, error: errorProp }) => {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [slaTier, setSlaTier] = useState('');
  const [page, setPage] = useState(1);

  // Debounce the search input → query key.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const selfFetch = clientsProp === undefined;
  const query = useQuery({
    queryKey: ['clients', 'list', { search, slaTier, page }],
    queryFn: () =>
      clientApi.listClients({
        search: search || undefined,
        slaTier: slaTier || undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
    enabled: selfFetch,
    staleTime: 15_000,
  });

  const isLoading = selfFetch ? query.isLoading : !!loadingProp;
  const error = selfFetch ? (query.error as Error | null) : errorProp ?? null;
  const items: Client[] = selfFetch ? (query.data?.items ?? []).map(toClient) : clientsProp ?? [];
  const total = selfFetch ? query.data?.total ?? 0 : (clientsProp?.length ?? 0);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const slaOptions = useMemo(
    () => [
      { value: '', label: 'All SLA tiers' },
      { value: 'Basic', label: 'Basic' },
      { value: 'Standard', label: 'Standard' },
      { value: 'Enterprise', label: 'Enterprise' },
    ],
    []
  );

  if (error) {
    return (
      <PageLayout title="Clients" icon={<Building2 className="h-6 w-6" />}>
        <ErrorState title="Failed to load clients" message={error.message} />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Clients"
      description={`${total} client${total === 1 ? '' : 's'} · synced from PersonalMed`}
      icon={<Building2 className="h-6 w-6" />}
      onRefresh={() => { void query.refetch(); }}
      isRefreshing={query.isFetching}
      lastFetchedAt={query.dataUpdatedAt}
      actions={<PageHelpPanel content={systemHelp} buttonSize="sm" />}
    >
      {/* Search + SLA filter */}
      <div className="mb-6 flex flex-wrap items-center gap-4">
        <AppTooltip title="Search clients by name or code">
          <Input
            placeholder="Search clients..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            leftAddon={<Search className="h-4 w-4" />}
            className="max-w-md"
          />
        </AppTooltip>
        <Select
          value={slaTier}
          onChange={(e) => {
            setSlaTier(e.target.value);
            setPage(1);
          }}
          options={slaOptions}
          className="w-48"
        />
      </div>

      {isLoading ? (
        <LoadingState rows={5} />
      ) : items.length > 0 ? (
        <>
          <div className="space-y-4">
            {items.map((client) => (
              <ClientCard key={client.id} client={client} />
            ))}
          </div>
          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <span className="text-sm text-[var(--color-text-secondary)]">
                Page {page} of {totalPages} · {total} total
              </span>
              <div className="flex gap-2">
                <Button variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  Previous
                </Button>
                <Button variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <EmptyState
          icon={<Building2 className="h-12 w-12" />}
          title="No clients found"
          message={
            search || slaTier
              ? 'No clients match your search/filter.'
              : 'Clients are synced from PersonalMed by the client_sync collector.'
          }
        />
      )}
    </PageLayout>
  );
};

export default ClientsPage;
